import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateVatFilingInput,
  UpdateVatFilingInput,
  VatFilingPeriod,
  VatRegisterResponse,
  VatSummaryResponse,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Per-tax-branch VAT breakdown (Phase 42-D). Mirrors the API shape; defined
// here because packages/shared is frozen for this phase. Money is wire-string.
export interface VatBranchBreakdownRow {
  branchId: string | null;
  outputVat: string;
  inputVat: string;
  payable: string;
}
export interface VatBranchBreakdownResponse {
  period: string;
  branches: VatBranchBreakdownRow[];
  totalOutputVat: string;
  totalInputVat: string;
  totalPayable: string;
}

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (same pattern as ar / period-close).
const registerKey = (companyId: string | undefined, period: string | undefined) =>
  ['vat-register', companyId, period] as const;
const summaryKey = (companyId: string | undefined, period: string | undefined) =>
  ['vat-summary', companyId, period] as const;
const filingsKey = (companyId: string | undefined, year: number | undefined) =>
  ['vat-filings', companyId, year] as const;
const branchBreakdownKey = (companyId: string | undefined, period: string | undefined) =>
  ['vat-branch-breakdown', companyId, period] as const;

export function useVatRegister(companyId: string | undefined, period: string | undefined) {
  return useQuery({
    queryKey: registerKey(companyId, period),
    queryFn: () =>
      api<VatRegisterResponse>(`/companies/${companyId}/vat/register?period=${period}`),
    enabled: !!companyId && !!period,
  });
}

export function useVatSummary(companyId: string | undefined, period: string | undefined) {
  return useQuery({
    queryKey: summaryKey(companyId, period),
    queryFn: () => api<VatSummaryResponse>(`/companies/${companyId}/vat/summary?period=${period}`),
    enabled: !!companyId && !!period,
  });
}

export function useVatBranchBreakdown(companyId: string | undefined, period: string | undefined) {
  return useQuery({
    queryKey: branchBreakdownKey(companyId, period),
    queryFn: () =>
      api<VatBranchBreakdownResponse>(`/companies/${companyId}/vat/${period}/branch-breakdown`),
    enabled: !!companyId && !!period,
  });
}

export function useVatFilings(companyId: string | undefined, year: number | undefined) {
  return useQuery({
    queryKey: filingsKey(companyId, year),
    queryFn: () => api<VatFilingPeriod[]>(`/companies/${companyId}/vat/filings?year=${year}`),
    enabled: !!companyId && year !== undefined,
  });
}

export function useCreateVatFiling(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateVatFilingInput) =>
      apiJson<CreateVatFilingInput, VatFilingPeriod>(
        'POST',
        `/companies/${companyId}/vat/filings`,
        data,
      ),
    onSuccess: (row) => {
      const year = Number(row.period.slice(0, 4));
      void qc.invalidateQueries({ queryKey: filingsKey(companyId, year) });
    },
  });
}

// File one DRAFT ภ.พ.30 per tax-branch with VAT activity in the period
// (Phase 42 optional-2). Returns the per-branch filings created/updated.
export function useFileVatBranches(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) =>
      apiJson<undefined, VatFilingPeriod[]>(
        'POST',
        `/companies/${companyId}/vat/${period}/file-branches`,
        undefined,
      ),
    onSuccess: (rows) => {
      const year = rows[0] ? Number(rows[0].period.slice(0, 4)) : undefined;
      if (year !== undefined) void qc.invalidateQueries({ queryKey: filingsKey(companyId, year) });
    },
  });
}

export function useUpdateVatFiling(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVatFilingInput }) =>
      apiJson<UpdateVatFilingInput, VatFilingPeriod>(
        'PATCH',
        `/companies/${companyId}/vat/filings/${id}`,
        data,
      ),
    onSuccess: (row) => {
      const year = Number(row.period.slice(0, 4));
      void qc.invalidateQueries({ queryKey: filingsKey(companyId, year) });
    },
  });
}
